import { PlayerCustomizationDTO } from "app/back-office/shared/models/basic-kustom/player-customization.model";
import { CustomizationRequestStatusEnum, CustomizationRequestStatusVMEnum } from "../enums/customization-request-status.enum";
import { ProductProjectTypeEnum } from "../enums/product-project-type.enum";
import { ExpiviPresetCard } from "./basic-kustom.models";
import { CustomizationFontDTO } from "./customization-font.model";
import { AttributeSerializedValue } from "./expivi.models";
import { ExpiviMapper } from "../utilities/expivi-mapper";

export class CustomizationRequestCreatorFormData {
  crName: string;
  comments: string;
  existingCr: CustomizationRequestDTO | null;
  customerRequesterId?: number;
  agencyRequesterId?: number;
  kustomPhone?: string;
  expiviPresetCardSelected: ExpiviPresetCard;
  defaultPreset: ExpiviPresetCard;
  crMessage: string;
  crMessageAttachment: File[];
  constructor(init?: Partial<CustomizationRequestCreatorFormData>) {
    Object.assign(this, init);
  }
}

export class CustomizationRequestDTO {
  channelName: string;
  customerName: string;
  pointOfSaleName: string;
  designerName: string;
  productCount: number;
  status: CustomizationRequestStatusEnum;
  /** Classificazione Evo/Subli; null/undefined per CR non ancora classificate */
  projectType?: ProductProjectTypeEnum;
  title: string;
  wholesaleTotalPrice: number;
  id: number;
  hasInternalUnreadMessages: boolean;
  hasDesignerUnreadMessages: boolean;
  lastDesignerReadyNotificationSentAt: string;
  constructor(init?: CustomizationRequestDTO) {
    Object.assign(this, init);
  }
}

export class CustomizationRequestVMDTO extends CustomizationRequestDTO {
  customStatus: CustomizationRequestStatusVMEnum;
  hasNotifications: boolean;
  constructor(init?: CustomizationRequestDTO) {
    super(init);
    this.customStatus = ExpiviMapper.MapCustomCRStatus(this.status, !!this.lastDesignerReadyNotificationSentAt);
    Object.assign(this, init);
    // La grid risolve i badge con `row[prop] * 1` e in JS `null * 1 === 0` (= Subli):
    // normalizziamo null -> undefined così una CR non classificata non mostra nessuna chip
    this.projectType = init?.projectType ?? undefined;
  }
}

export class CustomizationRequestDetailDTO {
  approvedCommerciallyByUser: UserInfoResponseDTO;
  approvedGraphicallyByUser: UserInfoResponseDTO;
  approvedByRequestingUser: UserInfoResponseDTO;
  assignedDesignerUser: UserInfoResponseDTO;
  isLockedForRequestingUser: boolean;
  rejectedAt: string;
  rejectedByUser: UserInfoResponseDTO;
  channelId: number;
  personalizedCost: number;
  shippingCost: number;
  channelName: string;
  commercialApprovalAt: string;
  commercialApprovalDeliveryDate: string;
  commercialApprovalFinancialDiscountPercentage: number;
  createdAt: string;
  createdByUser: UserInfoResponseDTO;
  currentUserOperationsAuthorization: CurrentUserOperationsAuthorization;
  customerId: number;
  customerName: string;
  graphicalApprovalAt: string;
  lastDesignerReadyNotificationSentAt: string;
  pointOfSaleId: number;
  pointOfSaleLocationAddress: string;
  pointOfSaleName: string;
  productCount: number;
  customerRequestingUser: UserInfoResponseDTO;
  agencyRequestingUser: UserInfoResponseDTO;
  requestingUserApprovalByUserId?: number;
  requestingUserApprovalAt: string;
  status: CustomizationRequestStatusEnum;
  storeId: number;
  storeName: string;
  tenantId: string;
  title: string;
  hasInternalUnreadMessages: boolean;
  hasDesignerUnreadMessages: boolean;
  id: number;
  totalQuantity: number;
  hasOrders: boolean;
  /** Primo ordine staccato per la CR (dalla timeline BE); assenti per CR ordinate prima di v6.7.8 */
  orderId?: number;
  orderIssuedAt?: string;
  orderIssuedByUser?: UserInfoResponseDTO;
  constructor(init?: CustomizationRequestDetailDTO) {
    Object.assign(this, init);
  }
}


export class MultiselectCustomizationRequestVm extends CustomizationRequestDTO {
  checked: boolean;
  constructor(init?: MultiselectCustomizationRequestVm) {
    super();
    Object.assign(this, init);
  }
}


export class CustomizationCreateRequestDTO {
  channelId: number;
  pointOfSaleId: number;
  customerRequestingUserId?: number;
  agencyRequestingUserId?: number;
  title: string;
  storeId: number;
  firstProduct?: CustomizedProductAdditionDTO;
  constructor(init?: CustomizationCreateRequestDTO) {
    Object.assign(this, init);
  }
}

export class CustomizedProductAdditionDTO {
  configuratorState?: ConfiguratorStateDto;
  configuratorThumbnail: string;
  configuratorThumbnailBack?: string; 
  templateId: string;
  isNewConfigRequest: boolean;
  newConfigRequestMessageContent?: string;
  productId: number;
  temporaryAttachmentUploadIds?: string[];
  comments?: string;
  fontId?: number;
  configuratorStateJson?: string;
  constructor(init?: CustomizedProductAdditionDTO) {
    Object.assign(this, init);
  }
}

export class ConfiguratorJsonStateLight {
  configured_products: {
    version: string;
    configuration: {
      attributes: AttributeSerializedValue[];
    };
  }[];
  constructor(init?: ConfiguratorJsonStateLight) {
    Object.assign(this, init);
  }
}

export class ConfiguratorStateDto {
  version: string;
  configuration: ConfigurationDTO;
  constructor(init?: ConfiguratorStateDto) {
    Object.assign(this, init);
  }
}
export class ConfigurationDTO {
  attributes: ConfiguratorStateAttributeDto[];
  constructor(init?: ConfigurationDTO) {
    Object.assign(this, init);
  }
}
export class ConfiguratorStateAttributeDto {
  id: number;
  name: string;
  value: AttributeValueDTO;
  valueName: string;
  constructor(init?: ConfiguratorStateAttributeDto) {
    Object.assign(this, init);
  }
}

export class AttributeValueDTO {
  imageSource: string;
  offset: number[];
  ratio: number;
  repeatType: string;
  rotation: number;
  scale: number;
  size: string;
  value: string;
  valueId: number;
  output: string;
  operands: AttributeValueOperandDTO[];
  calculatedValue: number;
  constructor(init?: AttributeValueDTO) {
    Object.assign(this, init);
  }
}
export class AttributeValueOperandDTO {
  step: number;
  value: number;
  constructor(init?: AttributeValueOperandDTO) {
    Object.assign(this, init);
  }
}

export class CustomizationRequestFormVm {
  crName: string;
  crMessage?: string;
  comments?: string;
  fontId?: number;
  existingCr?: CustomizationRequestDTO;
  customerRequesterId?: number;
  agencyRequesterId?: number;
  pointOfSaleId?: number;
  expiviPresetCardSelected?: ExpiviPresetCard;
  defaultPreset?: ExpiviPresetCard;
  crMessageAttachment?: File[];
  constructor(init?: CustomizationRequestFormVm) {
    Object.assign(this, init);
  }
}

export class CurrentUserOperationsAuthorization {
  canAddProduct: boolean;
  canChangeTitle: boolean;
  canApproveAsRequester: boolean;
  canApproveCommercially: boolean;
  canApproveGraphically: boolean;
  canReject: boolean;
  canChangePointOfSale: boolean;
  canChangeProductTemplate: boolean;
  canDelete: boolean;
  canDeleteProduct: boolean;
  canLockForRequester: boolean;
  canNotifyDesignerReady: boolean;
  canReassignDesigner: boolean;
  canReassignRequester: boolean;
  canSaveProductConfiguration: boolean;
  canUnlockForRequester: boolean;
  canAddMessages: boolean;
  constructor(init?: CurrentUserOperationsAuthorization) {
    Object.assign(this, init);
  }
}

export class UserInfoResponseDTO {
  email: string;
  id: number;
  name: string;
  roleName: string;
  constructor(init?: UserInfoResponseDTO) {
    Object.assign(this, init);
  }
}
export class CustomizedProductDTO {
  configuratorThumbnail: string;
  customThumbnailUrl?: string;
  externalConfigurationId: string;
  id: number;
  productId: number;
  productSku: string;
  productTitle: string;
  wholesaleTotalPrice: number;
  comments?: string;
  fontId?: number;
  isCustomizableWithPlayers: boolean;
  constructor(init?: CustomizedProductDTO) {
    Object.assign(this, init);
  }
}

export class CustomizedProductVM {
  uuid: string;
  totalCrProductWholeSalePrice: number;
  configuratorThumbnail: string;
  customThumbnailUrl?: string;
  externalConfigurationId: string;
  productId: number;
  productSku: string;
  productTitle: string;
  comments?: string;
  fontId?: number;
  id?: number;
  sizes?: CustomizedProductSizeVM[];
  selectedSize?: CustomizedProductSizeVM;
  approveColorCode?: string;
  approveColorName?: string;
  isCompliant?: boolean;
  graphicalApprovalDocument?: File;
  graphicalApprovalImage?: File;
  isReady?: boolean;
  isCustomizableWithPlayers: boolean;
  constructor(init?: CustomizedProductVM) {
    Object.assign(this, init);
  }
}

export class CustomizedProductDetailDTO {
  approvedColorCode: string;
  approvedColorName: string;
  configuratorState: ConfiguratorStateDto;
  configuratorStateJson: string;
  configuratorThumbnail: string;
  customThumbnailUrl?: string;
  customizedSizes: CustomizedProductSizeDTO[];
  externalConfigurationId: string;
  id: number;
  productDescription: string;
  isReadyForCommercialApproval: boolean;
  isReadyForGraphicalApproval: boolean;
  isReadyForRequestingUserApproval: boolean;
  isCustomizableWithPlayers: boolean;
  productId: number;
  productSku: string;
  productTitle: string;
  unitCustomizationPrice: number;
  comments: string;
  fontId: number;
  font: CustomizationFontDTO;
  constructor(init?: CustomizedProductDetailDTO) {
    Object.assign(this, init);
  }
}

export class CustomizedProductDetailVM extends CustomizedProductDetailDTO {
  declare customizedSizes: CustomizedProductSizeVM[];
  constructor(init?: CustomizedProductDetailVM) {
    super();
    Object.assign(this, init);
  }
}

export class CustomizedProductSizeDTO {
  id: number;
  requestedQuantity: number;
  playerCustomizations?: PlayerCustomizationDTO[];
  players?: PlayerCustomizationDTO[];
  sizeGroup: number;
  sizeId: number;
  sizeName: string;
  sizeOrder: number;
  variantId: number;
  wholesaleTotalPrice: number;
  wholesaleUnitPrice: number;
  constructor(init?: CustomizedProductSizeDTO) {
    Object.assign(this, init);
  }
}
export class CustomizedProductSizeVM extends CustomizedProductSizeDTO {
  fontId?: number;
  unitCustomizationPrice: number;
  previousApprovedQuantity?: number;
  checked?: boolean;
  productId?: number;
  constructor(init?: CustomizedProductSizeVM) {
    super();
    Object.assign(this, init);
  }
}

export class updateCrProductConfiguratorStateDTI {
  configuratorThumbnail: string;
  configuratorState: ConfiguratorStateDto;
  configuratorStateJson: string;
  constructor(init?: updateCrProductConfiguratorStateDTI) {
    Object.assign(this, init);
  }
}

export class ChangeCrProductTemplateDTI extends updateCrProductConfiguratorStateDTI {
  newTemplateId: number;
  constructor(init?: ChangeCrProductTemplateDTI) {
    super();
    Object.assign(this, init);
  }
}
export class RequesterApprovalResponseDto {
  customizedProducts: RequesterApprovalProductResponseDto[];
  constructor(init?: RequesterApprovalResponseDto) {
    Object.assign(this, init);
  }
}

export class RequesterApprovalProductResponseDto {
  configuratorThumbnail: string;
  customizableVariants: RequesterApprovalVariantResponseDto[];
  customizedProductId: number;
  externalConfigurationId: string;
  productId: number;
  productSku: string;
  productTitle: string;
  isCustomizableWithPlayers: boolean;
  comments: string;
  fontId: number;
  unitCustomizationPrice: number;
  approvedColorCode: string;
  approvedColorName: string;
  constructor(init?: RequesterApprovalProductResponseDto) {
    Object.assign(this, init);
  }
}

export class RequesterApprovalVariantResponseDto {
  previousApprovedQuantity: number;
  players: PlayerCustomizationDTO[];
  sizeGroup: number;
  sizeId: number;
  sizeName: string;
  sizeOrder: number;
  variantId: number;
  variantWholesaleUnitPrice: number;
  constructor(init?: RequesterApprovalVariantResponseDto) {
    Object.assign(this, init);
  }
}


export class RequesterApprovalDTI {
  approvedProducts: RequesterApprovalApprovedProductDTI[];
  constructor(init?: RequesterApprovalDTI) {
    Object.assign(this, init);
  }
}

export class RequesterApprovalApprovedProductDTI {
  approvedVariants: RequesterApprovalApprovedVariantDTI[];
  customizedProductId: number;
  fontId: number;
  constructor(init?: RequesterApprovalApprovedProductDTI
  ) {
    Object.assign(this, init);
  }
}

export class RequesterApprovalApprovedVariantDTI {
  approvedQuantity: number;
  variantId: number;
  playerCustomizations: PlayerCustomizationDTO[];
  constructor(init?: RequesterApprovalApprovedVariantDTI) {
    Object.assign(this, init);
  }
}

export class GraphicalApprovalResponseDto {
  customizedProducts: GraphicalApprovalProductResponseDto[];
  comments: string;
  constructor(init?: GraphicalApprovalResponseDto) {
    Object.assign(this, init);
  }
}

export class GraphicalApprovalProductResponseDto {
  configuratorThumbnail: string;
  customThumbnailUrl?: string;
  customizedProductId: number;
  externalConfigurationId: string;
  previousApprovedColorCode: string;
  previousApprovedColorName: string;
  productId: number;
  productSku: string;
  productTitle: string;
  wholesaleTotalPrice: number | null;
  comments?: string;
  isCustomizableWithPlayers: boolean;
  constructor(init?: GraphicalApprovalProductResponseDto) {
    Object.assign(this, init);
  }
}


export class GraphicalApprovalDTI {
  approvedProducts: GraphicalApprovalApprovedProductDTI[];
  constructor(init?: GraphicalApprovalDTI) {
    Object.assign(this, init);
  }
}

export class GraphicalApprovalApprovedProductDTI {
  approvedColorCode: string;
  approvedColorName: string;
  customizedProductId: number;
  temporaryDocumentAttachmentUploadId: string;
  temporaryImageAttachmentUploadId?: string;
  constructor(init?: GraphicalApprovalApprovedProductDTI
  ) {
    Object.assign(this, init);
  }
}


export class CommercialApprovalResponseDto {
  customizedProducts: CommercialApprovalProductResponseDto[];
  constructor(init?: CommercialApprovalResponseDto) {
    Object.assign(this, init);
  }
}

export class CommercialApprovalProductResponseDto {
  configuratorThumbnail: string;
  customizableVariants: CommercialApprovalVariantResponseDto[];
  customizedProductId: number;
  externalConfigurationId: string;
  productId: number;
  productSku: string;
  productTitle: string;
  unitCustomizationPrice: number;
  comments?: string;
  isCustomizableWithPlayers: boolean;
  constructor(init?: CommercialApprovalProductResponseDto) {
    Object.assign(this, init);
  }
}

export class CommercialApprovalVariantResponseDto {
  previousApprovedQuantity: number;
  sizeGroup: number;
  sizeId: number;
  sizeName: string;
  sizeOrder: number;
  variantId: number;
  variantWholesaleUnitPrice: number;
  constructor(init?: CommercialApprovalVariantResponseDto) {
    Object.assign(this, init);
  }
}


export class CommercialApprovalDTI {
  approvedDeliveryDate: string;
  approvedFinancialDiscountPercentage: number;
  personalizedCost: number;
  shippingCost: number;
  approvedProducts: CommercialApprovalApprovedProductDTI[];
  constructor(init?: CommercialApprovalDTI) {
    Object.assign(this, init);
  }
}

export class CommercialApprovalApprovedProductDTI {
  approvedVariants: CommercialApprovalApprovedVariantDTI[];
  customizedProductId: number;
  personalizedCost: number;
  constructor(init?: CommercialApprovalApprovedProductDTI
  ) {
    Object.assign(this, init);
  }
}

export class CommercialApprovalApprovedVariantDTI {
  approvedQuantity: number;
  variantId: number;
  playerCustomizations?: PlayerCustomizationDTO[];
  constructor(init?: CommercialApprovalApprovedVariantDTI) {
    Object.assign(this, init);
  }
}


export class MessageAttachmentResponseDto {
  id: number;
  attachedByUser: UserInfoResponseDTO;
  attachedToLogEntryId: number;
  fileNameWithExtension: string;
  publicUrl: string;
  isImage: boolean;
  constructor(init?: MessageAttachmentResponseDto) {
    Object.assign(this, init);
  }
}


// ============================================================
// BULK SIZE UPDATE – Request / Response
// Endpoint: POST /v1/customization-requests/{id}/products/sizes
// ============================================================

/** Etichetta testuale per il gruppo taglia (richiesta dall'API) */
export type SizeGroupLabel = 'Adult' | 'Youth' | 'Kid' | 'Infant';

/**
 * Singolo player all'interno di una size.
 * - `displayName` è l'unico campo obbligatorio
 */
export class BulkSizeUpdatePlayerRequest {
  number?: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  constructor(init?: Partial<BulkSizeUpdatePlayerRequest>) {
    Object.assign(this, init);
  }
}

/**
 * Singola size da inviare nel bulk update.
 * - **UPDATE**: `customizedProductSizeId` valorizzato (id esistente nel DB)
 * - **INSERT**: `customizedProductSizeId = null` + campi catalogo obbligatori
 */
export class BulkSizeUpdateSizeRequest {
  customizedProductSizeId?: number | null;

  // Campi catalogo – obbligatori solo per INSERT
  sizeId?: number;
  variantId?: number;
  sizeName?: string;
  sizeGroup?: SizeGroupLabel;
  sizeOrder?: number;
  wholesaleUnitPrice?: number;

  // Sempre obbligatori
  requestedQuantity: number;
  players: BulkSizeUpdatePlayerRequest[];

  constructor(init?: Partial<BulkSizeUpdateSizeRequest>) {
    Object.assign(this, init);
  }
}

/** Prodotto con le sue size da aggiornare in bulk */
export class BulkSizeUpdateProductRequest {
  customizedProductId: number;
  sizes: BulkSizeUpdateSizeRequest[];
  constructor(init?: Partial<BulkSizeUpdateProductRequest>) {
    Object.assign(this, init);
  }
}

/** Payload radice della POST /products/sizes */
export class BulkCustomizedProductSizesUpdateRequest {
  products: BulkSizeUpdateProductRequest[];
  constructor(init?: Partial<BulkCustomizedProductSizesUpdateRequest>) {
    Object.assign(this, init);
  }
}

// --- Response ---

/** Contatori riassuntivi dell'operazione bulk */
export class BulkSizeUpdateSummary {
  totalProcessed: number;
  successCount: number;
  failedCount: number;
  insertedCount: number;
  updatedCount: number;
  deletedCount: number;
  noChangeCount: number;
  constructor(init?: Partial<BulkSizeUpdateSummary>) {
    Object.assign(this, init);
  }
}

/** Risultato singolo per ogni size processata dal backend */
export class BulkSizeUpdateItemResult {
  customizedProductSizeId: number;
  customizedProductId: number;
  success: boolean;
  operationType: 'Updated' | 'Deleted' | 'NoChange' | null;
  oldQuantity: number | null;
  newQuantity: number | null;
  oldPlayerCount: number | null;
  newPlayerCount: number | null;
  errorMessage: string | null;
  resultMessage: string;
  constructor(init?: Partial<BulkSizeUpdateItemResult>) {
    Object.assign(this, init);
  }
}

/** Response completa della POST /products/sizes */
export class BulkCustomizedProductSizesUpdateResponse {
  summary: BulkSizeUpdateSummary;
  results: BulkSizeUpdateItemResult[];
  constructor(init?: Partial<BulkCustomizedProductSizesUpdateResponse>) {
    Object.assign(this, init);
  }
}

/**
 * Body della POST /v1/customization-requests/approved/{requestId}/close-as-ordered.
 * Chiude la CR approvata come `Ordered` registrando il carrello emesso;
 * l'email di notifica parte solo se `sendNotification` è true (opt-in lato BE).
 */
export interface CloseAsOrderedRequestBody {
  cartId: number;
  sendNotification: boolean;
}

/** Parametri query per GET /v1/customization-requests/report-excel */
export interface CrReportExcelParams {
  storeId: number;
  channelId?: number;
  customerId?: number;
  pointOfSaleId?: number;
  status?: CustomizationRequestStatusEnum;
  commercialApprovalByUserId?: number;
  createdByUserId?: number;
  designerUserId?: number;
  customerRequestingUserId?: number;
  agencyRequestingUserId?: number;
  agencyId?: number;
  searchString?: string;
}
